'use client';

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Typography } from '../../../components';
import { useTranslations } from '../../../contexts/translation.context';
import { usePageBreadcrumbs } from '../../../hooks/usePageBreadcrumbs';
import { FiltersSection, IncomeTable, StatsCards, PayoutHistoryModal } from './components';
import {
  IncomeFilters,
  EarnData,
  AvailableModel,
  AvailableCampaign,
  AvailableCplCampaign,
  EarnStats,
} from './types';
import { DEFAULT_FILTERS, DEFAULT_PAGE_SIZE, MOCK_EARNS } from './constants';
import { partnerEarnsService } from '../../../api/partner/partnerRevshareService';
import partnerRevshareService from '../../../api/partner/partnerRevshareService';
import partnerOfferService from '../../../api/partner/partnerOfferService';

const RevshareIncomePage: React.FC = () => {
  const { t } = useTranslations();

  // Breadcrumbs
  const breadcrumbs = useMemo(
    () => [
      { label: t('sidebar.dashboard') || 'Главная', href: '/partner/dashboard' },
      { label: 'Revshare', href: '/partner/revshare/offers' },
      { label: t('partner.revshare.income.title') || 'Доходы', isActive: true },
    ],
    [t]
  );
  usePageBreadcrumbs(breadcrumbs);

  // State
  const [isLoading, setIsLoading] = useState(false);
  const [earns, setEarns] = useState<EarnData[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [availableModels, setAvailableModels] = useState<AvailableModel[]>([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [availableCampaigns, setAvailableCampaigns] = useState<AvailableCampaign[]>([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [campaignsPage, setCampaignsPage] = useState(1);
  const [hasMoreCampaigns, setHasMoreCampaigns] = useState(true);
  const [campaignsTotalCount, setCampaignsTotalCount] = useState(0);
  const [availableCplCampaigns, setAvailableCplCampaigns] = useState<AvailableCplCampaign[]>([]);
  const [cplCampaignsLoading, setCplCampaignsLoading] = useState(false);
  const [cplCampaignsPage, setCplCampaignsPage] = useState(1);
  const [hasMoreCplCampaigns, setHasMoreCplCampaigns] = useState(true);
  const [cplCampaignsTotalCount, setCplCampaignsTotalCount] = useState(0);
  const campaignSearchRef = useRef('');
  const campaignSearchTimerRef = useRef<NodeJS.Timeout>();

  // Stats state
  const [stats, setStats] = useState<EarnStats>({
    total_amount_all: 0,
    total_amount_cpl: 0,
    total_spending: 0,
  });
  const [pendingAmount, setPendingAmount] = useState(0);
  const [refundedAmount, setRefundedAmount] = useState(0);

  // Modal state
  const [isPayoutHistoryOpen, setIsPayoutHistoryOpen] = useState(false);
  const [payoutHistory, setPayoutHistory] = useState<any[]>([]);
  const [isPayoutHistoryLoading, setIsPayoutHistoryLoading] = useState(false);

  // Filters
  const [filters, setFilters] = useState<IncomeFilters>(DEFAULT_FILTERS);

  // Calculate available amount
  const available = useMemo(() => {
    return stats.total_amount_all - stats.total_spending;
  }, [stats]);

  // Check if filters are active
  const hasActiveFilters = useMemo(() => {
    return (
      filters.account !== '' ||
      filters.campaign !== '' ||
      filters.grouping !== 'day' ||
      filters.limit !== '' ||
      filters.source !== ''
    );
  }, [filters]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const statsData = await partnerEarnsService.getEarnsStats();
      setStats(statsData);
      // TODO: Get pending and refunded amounts from API when available
      setPendingAmount(0);
      setRefundedAmount(0);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  // Fetch earns based on grouping
  const fetchEarns = useCallback(async () => {
    setIsLoading(true);
    try {
      const params: any = {
        page: currentPage,
        page_size: pageSize,
      };

      // Add model filter if selected
      if (filters.account && filters.account !== '') {
        const model = availableModels.find((m) => m.id === filters.account);
        if (model?.of_user_id) {
          params.model_id = model.of_user_id;
        }
      }

      // Add campaign filter if selected
      if (filters.campaign && filters.campaign !== '') {
        params.company_inner_id = filters.campaign;
      }

      // Add date range filter based on limit
      if (filters.limit && filters.limit !== '') {
        const today = new Date();
        const endDate = today.toISOString().split('T')[0]; // Format: YYYY-MM-DD
        let startDate: string;

        switch (filters.limit) {
          case '30days':
            const date30 = new Date(today);
            date30.setDate(date30.getDate() - 30);
            startDate = date30.toISOString().split('T')[0];
            break;
          case '90days':
            const date90 = new Date(today);
            date90.setDate(date90.getDate() - 90);
            startDate = date90.toISOString().split('T')[0];
            break;
          case 'year':
            const dateYear = new Date(today);
            dateYear.setFullYear(dateYear.getFullYear() - 1);
            startDate = dateYear.toISOString().split('T')[0];
            break;
          default:
            startDate = '';
        }

        if (startDate) {
          params.start_date = startDate;
          params.end_date = endDate;
        }
      }

      let response;

      if (filters.grouping === 'month') {
        response = await partnerEarnsService.getMonthlyEarns(params);
        // Transform monthly data to match EarnData interface
        const transformedData: EarnData[] = response.objects.map((item: any) => ({
          date: item.month,
          compaign_type: item.compaign_type,
          total_amount: item.total_amount,
          total_subscribers: item.total_subscribers,
        }));
        setEarns(transformedData);
        setTotalCount(response.count);
      } else if (filters.grouping === 'week') {
        response = await partnerEarnsService.getWeeklyEarns(params);
        // Transform weekly data to match EarnData interface
        const transformedData: EarnData[] = response.objects.map((item: any) => ({
          date: item.week,
          compaign_type: item.compaign_type,
          total_amount: item.total_amount,
          total_subscribers: item.total_subscribers,
        }));
        setEarns(transformedData);
        setTotalCount(response.count);
      } else {
        // Day grouping - use daily earns
        response = await partnerEarnsService.getDailyEarns(params);
        setEarns(response.objects);
        setTotalCount(response.count);
      }

      // Apply source filter client-side if needed (API might not support it directly)
      if (filters.source && filters.source !== '') {
        const sourceFilter = filters.source === 'cpl' ? 'cpl' : 'ref_share';
        const filteredEarns = earns.filter((e) => e.compaign_type === sourceFilter);
        setEarns(filteredEarns);
        setTotalCount(filteredEarns.length);
      }
    } catch (error) {
      console.error('Error fetching earns:', error);
      // Fallback to mock data on error
      let filteredEarns = [...MOCK_EARNS];

      // Apply source filter
      if (filters.source && filters.source !== '') {
        const sourceFilter = filters.source === 'cpl' ? 'cpl' : 'ref_share';
        filteredEarns = filteredEarns.filter((e) => e.compaign_type === sourceFilter);
      }

      setEarns(filteredEarns);
      setTotalCount(filteredEarns.length);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, filters, availableModels]);

  // Fetch available models from API
  const fetchAvailableModels = useCallback(async (search: string = '') => {
    setModelsLoading(true);
    try {
      const response = await partnerRevshareService.getCompaniesModels({
        page: 1,
        page_size: 50,
        search: search || undefined,
      });
      if (response.objects) {
        setAvailableModels(
          response.objects.map((acc) => ({
            id: acc._id,
            name: `${String(acc.of_user_id).slice(-4)}`,
            username: acc.of_user_name || '',
            avatar: acc.avatar || '',
            of_user_id: acc.of_user_id,
          }))
        );
      }
    } catch (error) {
      console.error('Error fetching models:', error);
    } finally {
      setModelsLoading(false);
    }
  }, []);

  // Fetch campaigns from orders (with pagination)
  const PAGE_SIZE_CAMPAIGNS = 20;

  const fetchCampaigns = useCallback(async (page: number = 1, search?: string) => {
    setCampaignsLoading(true);
    try {
      const ordersResponse = await partnerRevshareService.getOrders({
        page,
        page_size: PAGE_SIZE_CAMPAIGNS,
        ...(search ? { id: search } : {}),
      });

      const campaignsFromOrders: AvailableCampaign[] = ordersResponse.objects.map((order: any) => ({
        id: order._id,
        shortId: order._id,
        modelName: order.model?.of_user_name || order.model?.name || '',
        modelAvatar: order.model?.avatar,
        source: order.source || 'only_fans',
        createdAt: order.created_at,
        type:
          order.type === 'FREE' || order.type === 'FREE_TRIAL'
            ? ('ref_share' as const)
            : ('cpl' as const),
        ...order,
      }));

      if (page === 1) {
        setAvailableCampaigns(campaignsFromOrders);
      } else {
        setAvailableCampaigns((prev) => [...prev, ...campaignsFromOrders]);
      }

      setCampaignsTotalCount(ordersResponse.count);
      const totalLoaded = (page - 1) * PAGE_SIZE_CAMPAIGNS + campaignsFromOrders.length;
      setHasMoreCampaigns(totalLoaded < ordersResponse.count);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    } finally {
      setCampaignsLoading(false);
    }
  }, []);

  // Fetch CPL campaigns from CPL orders (with pagination)
  const fetchCplCampaigns = useCallback(async (page: number = 1, search?: string) => {
    setCplCampaignsLoading(true);
    try {
      const ordersResponse = await partnerOfferService.getOrders({
        page,
        page_size: PAGE_SIZE_CAMPAIGNS,
        ...(search ? { search } : {}),
      });

      const cplCampaigns: AvailableCplCampaign[] = ordersResponse.objects.map((order: any) => ({
        id: order.id || order._id,
        shortId: order.id || order._id,
        modelName: order.model?.of_user_name || '',
        modelAvatar: order.model?.avatar,
        source: order.offer?.source || 'only_fans',
        createdAt: order.created_at,
        type: 'cpl' as const,
        subsCount: order.subs?.count,
        status: order.status,
      }));

      if (page === 1) {
        setAvailableCplCampaigns(cplCampaigns);
      } else {
        setAvailableCplCampaigns((prev) => [...prev, ...cplCampaigns]);
      }

      setCplCampaignsTotalCount(ordersResponse.count);
      const totalLoaded = (page - 1) * PAGE_SIZE_CAMPAIGNS + cplCampaigns.length;
      setHasMoreCplCampaigns(totalLoaded < ordersResponse.count);
    } catch (error) {
      console.error('Error fetching CPL campaigns:', error);
    } finally {
      setCplCampaignsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchAvailableModels();
    fetchCampaigns();
    fetchCplCampaigns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchEarns();
  }, [fetchEarns]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleFilterChange = useCallback((key: keyof IncomeFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  }, []);

  const handleResetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  }, []);

  const handleModelSearchChange = useCallback(
    (search: string) => {
      fetchAvailableModels(search);
    },
    [fetchAvailableModels]
  );

  const handleLoadMoreCampaigns = useCallback(() => {
    if (campaignsLoading || !hasMoreCampaigns) return;
    const nextPage = campaignsPage + 1;
    setCampaignsPage(nextPage);
    fetchCampaigns(nextPage, campaignSearchRef.current || undefined);
  }, [campaignsLoading, hasMoreCampaigns, campaignsPage, fetchCampaigns]);

  const handleLoadMoreCplCampaigns = useCallback(() => {
    if (cplCampaignsLoading || !hasMoreCplCampaigns) return;
    const nextPage = cplCampaignsPage + 1;
    setCplCampaignsPage(nextPage);
    fetchCplCampaigns(nextPage, campaignSearchRef.current || undefined);
  }, [cplCampaignsLoading, hasMoreCplCampaigns, cplCampaignsPage, fetchCplCampaigns]);

  const handleSearchCampaigns = useCallback(
    (query: string) => {
      campaignSearchRef.current = query;
      if (campaignSearchTimerRef.current) clearTimeout(campaignSearchTimerRef.current);
      campaignSearchTimerRef.current = setTimeout(() => {
        setCampaignsPage(1);
        setCplCampaignsPage(1);
        fetchCampaigns(1, query || undefined);
        fetchCplCampaigns(1, query || undefined);
      }, 400);
    },
    [fetchCampaigns, fetchCplCampaigns]
  );

  const handleOpenPayoutHistory = useCallback(async () => {
    setIsPayoutHistoryOpen(true);
    setIsPayoutHistoryLoading(true);
    try {
      // TODO: Implement payout history API call when available
      // For now, use empty array
      setPayoutHistory([]);
    } catch (error) {
      console.error('Error fetching payout history:', error);
    } finally {
      setIsPayoutHistoryLoading(false);
    }
  }, []);

  const handleClosePayoutHistory = useCallback(() => {
    setIsPayoutHistoryOpen(false);
  }, []);

  return (
    <div className="w-full px-4 py-6 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <Typography variant="h4" className="mb-2">
          {t('partner.revshare.income.title')}
        </Typography>
      </div>

      {/* Stats Cards */}
      <div className="pb-6">
        <StatsCards
          totalEarned={stats.total_amount_all}
          pendingAmount={pendingAmount}
          refundedAmount={refundedAmount}
          totalPaid={stats.total_spending}
          available={available}
          onOpenPayoutHistory={handleOpenPayoutHistory}
        />
      </div>

      {/* Filters */}
      <div className=" mb-4">
        <FiltersSection
          filters={filters}
          availableModels={availableModels}
          availableCampaigns={availableCampaigns}
          availableCplCampaigns={availableCplCampaigns}
          isLoading={modelsLoading}
          isCampaignsLoading={campaignsLoading}
          isCplCampaignsLoading={cplCampaignsLoading}
          hasMoreCampaigns={hasMoreCampaigns}
          hasMoreCplCampaigns={hasMoreCplCampaigns}
          campaignsTotalCount={campaignsTotalCount}
          cplCampaignsTotalCount={cplCampaignsTotalCount}
          hasActiveFilters={hasActiveFilters}
          onFilterChange={handleFilterChange}
          onResetFilters={handleResetFilters}
          onModelSearchChange={handleModelSearchChange}
          onLoadMoreCampaigns={handleLoadMoreCampaigns}
          onLoadMoreCplCampaigns={handleLoadMoreCplCampaigns}
          onSearchCampaigns={handleSearchCampaigns}
        />
      </div>

      {/* Table */}
      <IncomeTable
        data={earns}
        isLoading={isLoading}
        totalCount={totalCount}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      {/* Payout History Modal */}
      <PayoutHistoryModal
        isOpen={isPayoutHistoryOpen}
        onClose={handleClosePayoutHistory}
        isLoading={isPayoutHistoryLoading}
        payouts={payoutHistory}
      />
    </div>
  );
};

export default RevshareIncomePage;
